/*!
 * hub-shoe-profile.js — parametric running-shoe side profile geometry.
 * ---------------------------------------------------------------------------
 * PROVENANCE: generated with Claude design for ATHLO+ and handed over as
 * "3D shoe model replacement.zip" on 2026-08-25; owner-confirmed as ours to
 * use. Vendored VERBATIM apart from this header — do not hand-edit. If the
 * profile needs changing, regenerate and re-vendor, or the next handover
 * silently reverts your edits.
 *
 * The zip also carried support.js and 12MB of reference PNGs. Neither ships:
 * this file is the whole dependency.
 *
 * WHY IT REPLACED THE OLD ANCHOR TABLE: model space is millimetres against a
 * fixed 270mm last (US M9), so 1 SVG unit = 1mm and the drawing is finally at
 * true scale — the old one used an arbitrary 1.9 units/mm. It also carries a
 * real toe spring, a sculpted midsole rocker and a proper heel counter, where
 * the old profile was a rounded slab with a blunt toe.
 *
 * API:  ShoeProfile.build({ heelStack, forefootStack, weight, plate, strike })
 *       -> { viewBox, paths, labels, metrics }.  Pure; safe per frame.
 * Paint order and key types are documented in hub-shoe-sim-v2.js's renderer.
 */
(function (root) {
  'use strict';

  var L = 270;          // last length, mm (US M9)
  var OX = 44;          // svg x of heel point
  var GY = 156;         // svg y of ground line
  var VW = 380, VH = 210;

  function num(v, d) { v = parseFloat(v); return isFinite(v) ? v : d; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function smoothstep(t) { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); }
  function ease(t, p) { return Math.pow(clamp(t, 0, 1), p); }
  function f(n) { return Math.round(n * 100) / 100; }
  // one-sided smooth ramp: 0 for u<=0, ~u for large u, rounded corner near 0
  function sp(u, k) { return Math.max(0, 0.5 * (u + Math.sqrt(u * u + k)) - 0.5 * Math.sqrt(k)); }

  function sx(x) { return OX + x; }
  function sy(y) { return GY - y; }

  // ---- path helpers ---------------------------------------------------
  function curve(pts, close) {
    if (!pts || pts.length < 2) return '';
    var d = 'M' + f(sx(pts[0][0])) + ' ' + f(sy(pts[0][1]));
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i - 1] || pts[i], p1 = pts[i],
          p2 = pts[i + 1], p3 = pts[i + 2] || pts[i + 1];
      var c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
      var c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += 'C' + f(sx(c1x)) + ' ' + f(sy(c1y)) + ',' +
                 f(sx(c2x)) + ' ' + f(sy(c2y)) + ',' +
                 f(sx(p2[0])) + ' ' + f(sy(p2[1]));
    }
    return close ? d + 'Z' : d;
  }

  function band(top, bot) {
    var a = curve(top, false);
    var b = curve(bot.slice().reverse(), false);
    return a + 'L' + b.slice(1) + 'Z';
  }

  // closed band with rounded end caps (foam / rubber edge radius)
  function capped(top, bot, r) {
    var t0 = top[0], t1 = top[top.length - 1];
    var b0 = bot[0], b1 = bot[bot.length - 1];
    var rt = Math.min(r, (t1[1] - b1[1]) * 0.4), rh = Math.min(r, (t0[1] - b0[1]) * 0.4);
    var toe = [[t1[0] + r * 0.85, t1[1] - rt], [t1[0] + r * 1.1, (t1[1] + b1[1]) / 2], [t1[0] + r * 0.85, b1[1] + rt]];
    var heel = [[b0[0] - r * 0.85, b0[1] + rh], [b0[0] - r * 1.1, (t0[1] + b0[1]) / 2], [b0[0] - r * 0.85, t0[1] - rh]];
    return curve(top.concat(toe, bot.slice().reverse(), heel), true);
  }

  function line(pts) {
    var d = '';
    for (var i = 0; i < pts.length; i++)
      d += (i ? 'L' : 'M') + f(sx(pts[i][0])) + ' ' + f(sy(pts[i][1]));
    return d;
  }

  function sample(x0, x1, step, fn) {
    var pts = [], n = Math.max(2, Math.ceil(Math.abs(x1 - x0) / step));
    for (var i = 0; i <= n; i++) { var x = lerp(x0, x1, i / n); pts.push([x, fn(x)]); }
    return pts;
  }

  function offsetPoly(pts, d) {
    var out = [];
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i], a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
      var dx = b[0] - a[0], dy = b[1] - a[1], m = Math.sqrt(dx * dx + dy * dy) || 1;
      out.push([p[0] - dy / m * d, p[1] + dx / m * d]);
    }
    return out;
  }

  function interp(list, x) {
    if (x <= list[0][0]) return list[0][1];
    for (var i = 0; i < list.length - 1; i++) {
      if (x <= list[i + 1][0]) {
        var t = (x - list[i][0]) / (list[i + 1][0] - list[i][0]);
        return lerp(list[i][1], list[i + 1][1], smoothstep(t));
      }
    }
    return list[list.length - 1][1];
  }

  // Upper silhouette: height above the midsole topline, by x.
  // Collar rear ~63 mm over the footbed, eyelet stay ~22, toe box ~18.
  var UPPER_H = [
    [1, 0.6], [6, 16], [12, 36], [19, 52], [27, 61.5], [36, 65], [46, 64.2],
    [58, 61], [72, 56.5], [86, 51.5], [100, 46], [114, 42], [130, 39],
    [146, 36.5], [164, 34], [182, 32], [200, 29.5], [216, 26], [230, 21.5],
    [242, 15.5], [252, 9], [260, 3.5], [266, 0.4]
  ];

  // Anatomical foot, height above the footbed, side view.
  var FOOT = [
    [28, 2.5], [42, 1.6], [58, 2.6], [76, 5.6], [94, 8.2], [112, 8.6],
    [132, 6.8], [152, 4.2], [172, 2.2], [192, 1.3], [212, 1.2], [228, 1.6],
    [238, 2.8], [245, 5.6], [246, 9.2],
    [238, 11], [224, 12.4], [206, 14], [186, 16.6], [166, 20.2],
    [148, 25], [128, 31.4], [110, 36], [94, 42], [78, 49], [64, 55.5],
    [54, 60], [50, 66], [48, 72], [46.5, 76], [43, 78], [26, 78], [22, 74], [20, 68],
    [19, 58], [20, 47], [22, 35], [24, 22], [26, 11]
  ];

  function build(o) {
    o = o || {};
    var heel = clamp(num(o.heelStack, 14), 4, 60);
    var fore = clamp(num(o.forefootStack, 6), 2, 55);
    var weight = clamp(num(o.weight, 210), 120, 400);
    var plate = o.plate || 'none';
    var strike = o.strike || 'heel';
    var drop = heel - fore;

    var xH = 0.09 * L, xB = 0.70 * L;
    var bevelDeg = clamp(9 + heel * 0.30, 11, 27);
    var tanB = Math.tan(bevelDeg * Math.PI / 180);
    var xC0 = clamp(0.05 * L + heel * 0.42, 15, 54);   // heel contact point
    var toeRise = clamp(5.5 + 0.33 * heel + 0.16 * fore, 8, 34);
    var xTS = clamp(0.80 * L - heel * 0.62, 0.60 * L, 0.86 * L);
    var outScale = clamp(Math.min(heel, fore) / 13, 0.40, 1.15);
    var tsDen = sp(L - xTS, 26);

    // ground contour = underside of the outsole
    function botAt(x) {
      return tanB * sp(xC0 - x, 40) + toeRise * Math.pow(sp(x - xTS, 26) / tsDen, 1.85);
    }
    // outsole rubber thickness
    function outT(x) {
      var u = x / L;
      var t = 4.3 - 1.9 * smoothstep((u - 0.26) / 0.16)
                  + 1.6 * smoothstep((u - 0.52) / 0.14)
                  - 1.5 * smoothstep((u - 0.85) / 0.13);
      return t * outScale;
    }
    // footbed / top of the midsole
    function topAt(x) {
      if (x <= xH) return heel + (xH - x) * 0.11;
      if (x <= xB) {
        var t = (x - xH) / (xB - xH);
        return lerp(heel, fore, 0.5 * smoothstep(t) + 0.5 * t)
             + 4.6 * Math.sin(Math.PI * Math.pow(t, 1.15));
      }
      var s = (x - xB) / (L - xB);
      return botAt(x) + lerp(fore, fore * 0.52, ease(s, 1.35));
    }
    function msBot(x) { return botAt(x) + 0.35; }
    function stackAt(x) { return topAt(x) - botAt(x); }

    var P = {};

    // ---- midsole ------------------------------------------------------
    var msTop = sample(-2, L - 7, 2.5, topAt);
    var msBotP = sample(-2, L - 7, 2.5, msBot);
    P.midsole = capped(msTop, msBotP, 4.5);

    P.sidewall = [
      // foam parting line — two-density split, follows the stack
      curve(sample(0, 0.94 * L, 4, function (x) { return botAt(x) + stackAt(x) * 0.58; })),
      // top chamfer where the foam meets the lasting margin
      curve(sample(2, 0.90 * L, 4, function (x) { return topAt(x) - Math.min(5.5, stackAt(x) * 0.20); }))
    ];

    // ---- outsole ------------------------------------------------------
    var splitPads = weight < 255 || plate === 'carbon';
    var pads = splitPads ? [[3, 0.44 * L], [0.545 * L, L - 8]] : [[3, L - 8]];
    P.outsole = [];
    P.tread = [];
    pads.forEach(function (pad) {
      var b = sample(pad[0], pad[1], 2.5, botAt);
      var t = sample(pad[0], pad[1], 2.5, function (x) { return botAt(x) + outT(x); });
      P.outsole.push(capped(t, b, 1.9));
      var step = pad[0] > 0.5 * L ? 12 : 15;
      for (var x = pad[0] + 9; x < pad[1] - 6; x += step) {
        P.tread.push(line([[x, botAt(x) + 0.3], [x + 2.6, botAt(x + 2.6) + outT(x) * 0.72]]));
      }
    });

    // ---- plate --------------------------------------------------------
    if (plate === 'carbon' || plate === 'nylon') {
      var th = plate === 'carbon' ? 1.35 : 2.2;
      function plateAt(x) {
        var u = x / L, fr;
        if (u < 0.20) fr = lerp(0.88, 0.50, smoothstep(u / 0.20));
        else if (u < 0.62) fr = lerp(0.50, 0.38, smoothstep((u - 0.20) / 0.42));
        else fr = lerp(0.38, 0.54, smoothstep((u - 0.62) / 0.30));
        return botAt(x) + stackAt(x) * fr;
      }
      var pa = 0.035 * L, pb = 0.93 * L;
      P.plate = capped(sample(pa, pb, 3, function (x) { return plateAt(x) + th / 2; }),
                       sample(pa, pb, 3, function (x) { return plateAt(x) - th / 2; }), th * 0.5);
    } else P.plate = null;

    // ---- upper --------------------------------------------------------
    function uh(x) { return interp(UPPER_H, x); }
    function uTop(x) { return topAt(x) + uh(x); }
    var upOut = sample(4, 267, 3, uTop);
    var upBot = sample(4, 267, 3, topAt);
    P.upper = band(upOut, upBot);

    // heel counter — internal stiffener
    var ctrTop = [[3, topAt(3) + 2], [6.5, topAt(6.5) + 17], [10, topAt(10) + 32],
                  [14, topAt(14) + 41], [20, topAt(20) + 43.5], [30, topAt(30) + 41],
                  [44, topAt(44) + 33], [58, topAt(58) + 22], [70, topAt(70) + 11],
                  [78, topAt(78) + 2.5]];
    P.heelCounter = band(ctrTop, sample(3, 78, 3, topAt));
    P.counterSeam = curve(ctrTop);

    // toe cap
    var capSeam = [[198, topAt(198) + 2], [209, topAt(209) + 11], [219, topAt(219) + 19],
                   [228, uTop(228) - 1]];
    var capOut = capSeam.concat([[236, uTop(236)], [246, uTop(246)], [256, uTop(256)], [262, uTop(262)], [266, uTop(266)]]);
    P.toeCap = band(capOut, sample(198, 266, 3, topAt));
    P.toeSeam = curve(capSeam);

    // blank branding panel, midfoot
    P.brandPanel = curve([[104, topAt(104) + 10], [108, topAt(108) + 27], [134, topAt(134) + 27],
                          [162, topAt(162) + 21], [170, topAt(170) + 12], [160, topAt(160) + 7],
                          [126, topAt(126) + 6], [110, topAt(110) + 7]], true);

    // collar padding
    var collarRun = [13, 17, 22, 29, 38, 50, 64, 80, 98, 114].map(function (x) {
      return [x, uTop(x) - 1.3];
    });
    var inner = offsetPoly(collarRun, -6.5);
    var nI = inner.length;
    [[0, 0], [1, 0.45], [nI - 2, 0.45], [nI - 1, 0]].forEach(function (q) {
      inner[q[0]] = [lerp(collarRun[q[0]][0], inner[q[0]][0], q[1]),
                     lerp(collarRun[q[0]][1], inner[q[0]][1], q[1])];
    });
    P.collarPad = band(collarRun, inner);

    // tongue — padded flap, leans back over the throat
    P.tongue = curve([[98, topAt(98) + 24], [80, topAt(80) + 34], [69, topAt(69) + 47],
                      [66, topAt(66) + 57], [72, topAt(72) + 61.5], [82, topAt(82) + 58],
                      [92, topAt(92) + 50], [102, topAt(102) + 39], [106, topAt(106) + 29]], true);

    // ---- lacing -------------------------------------------------------
    var eyeXs = [110, 124, 138, 152, 165, 176];
    P.eyelets = eyeXs.map(function (x) {
      return { cx: f(sx(x)), cy: f(sy(uTop(x) - 6.5)), r: 1.7 };
    });
    var e = eyeXs.map(function (x) { return [x, uTop(x) - 6.5]; });
    P.laces = [];
    for (var i = 0; i < e.length - 2; i++) {
      var a = e[i], b2 = e[i + 2];
      var mx = (a[0] + b2[0]) / 2, my = (a[1] + b2[1]) / 2 + 13;
      P.laces.push('M' + f(sx(a[0])) + ' ' + f(sy(a[1])) +
                   'Q' + f(sx(mx)) + ' ' + f(sy(my)) + ',' + f(sx(b2[0])) + ' ' + f(sy(b2[1])));
    }
    for (var j = 0; j < e.length - 1; j++) {
      var a2 = e[j], b3 = e[j + 1];
      P.laces.push('M' + f(sx(a2[0])) + ' ' + f(sy(a2[1])) +
                   'Q' + f(sx((a2[0] + b3[0]) / 2)) + ' ' + f(sy((a2[1] + b3[1]) / 2 + 3.4)) +
                   ',' + f(sx(b3[0])) + ' ' + f(sy(b3[1])));
    }

    // ---- stitching ----------------------------------------------------
    P.seams = [
      curve(sample(9, 262, 4, function (x) { return topAt(x) + 3.2; })),  // lasting margin
      curve(sample(100, 186, 4, function (x) { return uTop(x) - 10.5; })), // eyelet stay
      P.counterSeam,
      P.toeSeam
    ];

    // ---- foot & load path ---------------------------------------------
    P.foot = curve(FOOT.map(function (p) { return [p[0], topAt(p[0]) + p[1] + 3.5]; }), true);
    var strikeX = strike === 'fore' ? 0.66 * L : strike === 'mid' ? 0.44 * L : 0.15 * L;
    P.loadPath = curve(sample(strikeX, 0.90 * L, 4, function (x) { return botAt(x) - 4.5; }));
    var lx = 0.90 * L, ly = botAt(lx) - 4.5, la = Math.atan2(botAt(lx) - botAt(lx - 6), 6);
    P.loadArrow = 'M' + f(sx(lx)) + ' ' + f(sy(ly)) +
                  'L' + f(sx(lx - 7 * Math.cos(la) - 2.6 * Math.sin(la))) + ' ' + f(sy(ly - 7 * Math.sin(la) + 2.6 * Math.cos(la))) +
                  'L' + f(sx(lx - 7 * Math.cos(la) + 2.6 * Math.sin(la))) + ' ' + f(sy(ly - 7 * Math.sin(la) - 2.6 * Math.cos(la))) + 'Z';
    P.strikeMark = { cx: f(sx(strikeX)), cy: f(sy(botAt(strikeX) - 4.5)), r: 3.4 };
    P.strikeArrow = line([[strikeX, botAt(strikeX) + 26], [strikeX, botAt(strikeX) + 12]]);

    // ---- technical layer ------------------------------------------------
    P.ground = line([[-24, 0], [L + 34, 0]]);
    var g = '';
    for (var t2 = 0; t2 <= L; t2 += 10) {
      var big = t2 % 50 === 0;
      g += 'M' + f(sx(t2)) + ' ' + f(sy(0)) + 'L' + f(sx(t2)) + ' ' + f(sy(big ? -5 : -2.5));
    }
    P.grid = g;
    P.limit = line([[-14, 40], [L + 14, 40]]);
    P.guides = [
      line([[xH, -6], [xH, heel + 74]]),
      line([[xB, -6], [xB, topAt(xB) + 40]])
    ];
    P.dims = [
      line([[xH, 0], [xH, heel]]),
      line([[xH - 3.5, 0], [xH + 3.5, 0]]),
      line([[xH - 3.5, heel], [xH + 3.5, heel]]),
      line([[xB, 0], [xB, fore]]),
      line([[xB - 3.5, 0], [xB + 3.5, 0]]),
      line([[xB - 3.5, fore], [xB + 3.5, fore]]),
      line([[L + 22, fore], [L + 22, heel]]),
      line([[L + 18, fore], [L + 26, fore]]),
      line([[L + 18, heel], [L + 26, heel]]),
      line([[xB, fore], [L + 22, fore]]),
      line([[xH, heel], [L + 22, heel]]),
      line([[0, -26], [L, -26]]),
      line([[0, -22], [0, -30]]),
      line([[L, -22], [L, -30]])
    ];
    P.angles = [
      line([[xRearAngle(), 0], [xC0, 0]]),
      line([[2, botAt(2)], [xC0, 0]]),
      line([[xTS, 0], [L, 0]]),
      line([[xTS, 0], [L, botAt(L)]])
    ];
    function xRearAngle() { return 2; }

    var toeDeg = Math.atan2(toeRise, L - xTS) * 180 / Math.PI;
    var reads = heel > 40 ? 'MAXIMALIST, OVER THE 40 MM ROAD LIMIT'
              : heel >= 36 ? 'MAX CUSHION (36–40 MM)'
              : heel >= 28 ? 'CUSHIONED (28–36 MM)'
              : heel >= 20 ? 'MODERATE (20–28 MM)'
              : heel >= 9 ? 'MINIMAL (9–20 MM)' : 'BAREFOOT (UNDER 9 MM)';

    var labels = [
      { x: f(sx(xH) - 6), y: f(sy(heel / 2) + 3), text: f(heel) + ' mm', anchor: 'end', kind: 'dim' },
      { x: f(sx(xB) - 6), y: f(sy(fore / 2) + 3), text: f(fore) + ' mm', anchor: 'end', kind: 'dim' },
      { x: f(sx(L + 30)), y: f(sy((heel + fore) / 2) + 3), text: f(drop) + ' drop', anchor: 'start', kind: 'accent' },
      { x: f(sx(L / 2)), y: f(sy(-26) + 12), text: L + ' mm  ·  US M9 LAST', anchor: 'middle', kind: 'dim' },
      { x: f(sx(L + 16)), y: f(sy(40) - 4), text: '40 mm limit', anchor: 'end', kind: 'warn' },
      { x: f(sx(xTS + 14)), y: f(sy(botAt(xTS + 14)) + 11), text: f(toeDeg) + '° toe spring', anchor: 'start', kind: 'dim' },
      { x: f(sx(xC0 + 4)), y: f(sy(tanB * (xC0 - 2) * 0.55) - 2), text: f(bevelDeg) + '° bevel', anchor: 'start', kind: 'dim' }
    ];

    return {
      viewBox: '0 0 ' + VW + ' ' + VH,
      width: VW, height: VH, length: L,
      paths: P,
      labels: labels,
      metrics: {
        heelStack: heel, forefootStack: fore, drop: drop, weight: weight,
        plate: plate, strike: strike,
        toeSpring: Math.round(toeDeg * 10) / 10,
        heelBevel: Math.round(bevelDeg * 10) / 10,
        contactX: Math.round(xC0), toeOffX: Math.round(xTS),
        reads: reads, legal: heel <= 40, splitOutsole: splitPads
      }
    };
  }

  var API = { build: build, LENGTH: L, viewBox: '0 0 ' + VW + ' ' + VH };
  root.ShoeProfile = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : this);
